import { ChecklistTemplate } from './types';

/**
 * Plantilla de revisión para SISTEMAS DE EXTINCIÓN AUTOMÁTICA
 * MEDIANTE AGENTES GASEOSOS (FM-200, Novec 1230, CO2, IG-541, IG-100…).
 *
 * Referencias normativas:
 *   - RD 513/2017 (RIPCI), Anexo II.
 *   - UNE-EN 15004 (sistemas fijos de extinción por agentes gaseosos).
 *   - UNE 23570 / UNE-EN 12094 (componentes de sistemas de extinción).
 *   - UNE-EN 54 (componentes de detección de incendios).
 *
 * Periodicidad: T = Trimestral · A = Anual.
 */
export const EXTINCION_GASEOSA_TEMPLATE: ChecklistTemplate = {
  tipo_sistema: 'pci_extincion_gaseosa',
  titulo: 'Extinción automática por agentes gaseosos',
  protocolos: [
    {
      id: 'inventario',
      titulo: '1. Inventario y configuración general',
      cabecera: [
        { key: 'agente', label: 'Agente extintor (FM-200 / Novec / CO₂ / IG-541…)' },
        { key: 'num_recintos', label: 'Nº de recintos protegidos', tipo: 'number' },
        { key: 'num_cilindros', label: 'Nº de cilindros', tipo: 'number' },
        { key: 'concentracion_diseno', label: 'Concentración de diseño (%)' },
        { key: 'tiempo_descarga_s', label: 'Tiempo de descarga (s)' },
      ],
      preguntas: [
        { id: 'IN.01', texto: '¿El inventario físico coincide con planos y proyecto técnico?', periodicidad: ['A'] },
        { id: 'IN.02', texto: '¿La concentración de diseño cumple con UNE-EN 15004 para el agente y riesgo?', periodicidad: ['A'] },
        { id: 'IN.03', texto: '¿Existe esquema funcional visible en sala técnica / recinto protegido?', periodicidad: ['A'] },
        { id: 'IN.04', texto: '¿El recinto mantiene su estanqueidad (ensayo Door-fan vigente, ≤ 10 años)?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'central',
      titulo: '2. Central de detección y extinción',
      cabecera: [
        { key: 'ubicacion', label: 'Ubicación' },
        { key: 'marca', label: 'Marca' },
        { key: 'modelo', label: 'Modelo' },
        { key: 'num_lazos', label: 'Nº de lazos', tipo: 'number' },
        { key: 'capacidad_lazo', label: 'Capacidad por lazo', tipo: 'number' },
        { key: 'firmware', label: 'Versión firmware' },
        { key: 'baterias_cantidad', label: 'Baterías · cantidad', tipo: 'number' },
        { key: 'baterias_v', label: 'Baterías · V', tipo: 'number' },
        { key: 'baterias_ah', label: 'Baterías · Ah', tipo: 'number' },
        { key: 'baterias_fecha', label: 'Baterías · fecha instalación' },
      ],
      preguntas: [
        { id: 'CN.01', texto: '¿La central está libre de averías, prealarmas o fallos pendientes?', periodicidad: ['T','A'] },
        { id: 'CN.02', texto: '¿El doble criterio cruzado de disparo (zona A + zona B) está correctamente programado?', periodicidad: ['A'] },
        { id: 'CN.03', texto: '¿La maniobra de bloqueo / abort y de disparo manual está operativa y señalizada?', periodicidad: ['T','A'] },
        { id: 'CN.04', texto: '¿La tensión de las baterías está dentro del rango (sin sulfatación) y antigüedad ≤ 4 años?', periodicidad: ['A'] },
        { id: 'CN.05', texto: '¿El paro de servocios (HVAC, compuertas) actúa correctamente al disparo?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'deteccion',
      titulo: '3. Elementos de detección y maniobra',
      preguntas: [
        { id: 'DT.01', texto: '¿Los detectores (ópticos / térmicos / multicriterio) están libres de polvo y se han probado?', periodicidad: ['T','A'], unidades: true },
        { id: 'DT.02', texto: '¿Los detectores por aspiración (DHA) están operativos: caudal, sensibilidad y orificios sin obstruir?', periodicidad: ['T','A'], unidades: true },
        { id: 'DT.03', texto: '¿Las barreras lineales (alineación, alcance, altura) están correctamente ajustadas?', periodicidad: ['T','A'], unidades: true },
        { id: 'DT.04', texto: '¿Los pulsadores manuales de disparo y bloqueo están accesibles, identificados y operativos?', periodicidad: ['T','A'], unidades: true },
        { id: 'DT.05', texto: '¿Las sirenas / sirena-flash interiores y exteriores de prealarma y descarga funcionan?', periodicidad: ['T','A'], unidades: true },
        { id: 'DT.06', texto: '¿Los módulos de entrada / salida y maniobra activan correctamente las cargas asociadas?', periodicidad: ['T','A'], unidades: true },
      ],
    },
    {
      id: 'fuentes',
      titulo: '4. Fuentes de alimentación auxiliares',
      preguntas: [
        { id: 'FA.01', texto: '¿Las fuentes auxiliares están supervisadas y sin fallos en central?', periodicidad: ['T','A'], unidades: true },
        { id: 'FA.02', texto: '¿La tensión en bornas y baterías está dentro del rango del fabricante?', periodicidad: ['T','A'], unidades: true },
        { id: 'FA.03', texto: '¿Las baterías tienen antigüedad ≤ 4 años y fecha visible?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'cilindros',
      titulo: '5. Cilindros / botellas de agente extintor',
      cabecera: [
        { key: 'agente_extintor', label: 'Agente extintor' },
        { key: 'numero_serie', label: 'Nº Serie' },
        { key: 'fecha_ultimo_retimbre', label: 'Último retimbre (mm/aaaa)' },
        { key: 'fecha_proximo_retimbre', label: 'Próximo retimbre (mm/aaaa)' },
        { key: 'fecha_fabricacion', label: 'Fecha fabricación (mm/aaaa)' },
      ],
      preguntas: [
        { id: 'CL.01', texto: '¿La presión de cada cilindro está dentro del rango (manómetro o pesaje según agente)?', periodicidad: ['T','A'], unidades: true },
        { id: 'CL.02', texto: '¿La pérdida de masa / presión es ≤ 5 % respecto al valor nominal?', periodicidad: ['T','A'], unidades: true },
        { id: 'CL.03', texto: '¿Los cilindros están correctamente anclados, sin corrosión y con placa identificativa legible?', periodicidad: ['A'], unidades: true },
        { id: 'CL.04', texto: '¿La fecha de retimbrado está vigente (≤ 10 años desde último retimbre)?', periodicidad: ['A'], unidades: true },
        { id: 'CL.05', texto: '¿La cabeza de disparo (actuador eléctrico / neumático / manual) está operativa y precintada?', periodicidad: ['T','A'], unidades: true },
        { id: 'CL.06', texto: '¿Los manguitos flexibles y latiguillos están en buen estado y dentro de su vida útil?', periodicidad: ['A'], unidades: true },
      ],
    },
    {
      id: 'red',
      titulo: '6. Red de tubería y difusores',
      preguntas: [
        { id: 'RD.01', texto: '¿La tubería está libre de corrosión, golpes y deformaciones; soportes correctos?', periodicidad: ['A'] },
        { id: 'RD.02', texto: '¿Los difusores están orientados según proyecto, libres de obstrucciones y sin pintura?', periodicidad: ['T','A'], unidades: true },
        { id: 'RD.03', texto: '¿La identificación y numeración de boquillas / difusores es legible?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'recinto',
      titulo: '7. Recinto protegido y elementos pasivos',
      preguntas: [
        { id: 'RC.01', texto: '¿La señalización de "Riesgo de descarga - No entrar" está colocada en todos los accesos?', periodicidad: ['A'] },
        { id: 'RC.02', texto: '¿Las compuertas de sobrepresión (PRV) están operativas y libres de obstrucción?', periodicidad: ['A'] },
        { id: 'RC.03', texto: '¿Los pasos de cables / sellados cortafuegos del recinto están íntegros?', periodicidad: ['A'] },
        { id: 'RC.04', texto: '¿El ensayo de estanqueidad (Door-fan test) está vigente (≤ 10 años o tras modificaciones)?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'documentacion',
      titulo: '8. Documentación y registros',
      preguntas: [
        { id: 'DC.01', texto: '¿Existe acta firmada de la última revisión por empresa mantenedora habilitada?', periodicidad: ['A'] },
        { id: 'DC.02', texto: '¿Está actualizado el inventario con marca, modelo, ubicación y Nº de serie?', periodicidad: ['A'] },
        { id: 'DC.03', texto: '¿Se conservan certificados de retimbrado y ensayo de estanqueidad?', periodicidad: ['A'] },
        { id: 'DC.04', texto: '¿Se ha entregado al titular el informe de anomalías y acciones correctoras?', periodicidad: ['A'] },
      ],
    },
  ],
};
